import React, { useState, useEffect } from 'react';
import { tendersAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['#00d4a1', '#4f8ef7', '#ff6b35', '#ff4757', '#a55eea', '#00b8d9', '#ffd32a', '#0be881'];

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    tendersAPI.analytics().then(res => setData(res.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px', color: 'var(--text-muted)', flexDirection: 'column', gap: '12px' }}>
      <div style={{ width: '24px', height: '24px', border: '2px solid rgba(0,212,161,0.2)', borderTopColor: 'var(--accent-green)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{'@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}'}</style>
      Loading analytics...
    </div>
  );

  // No data yet — show empty state with helpful prompt
  if (!data || (!data.by_category?.length && !data.by_state?.length)) return (
    <div>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '26px', marginBottom: '6px' }}>Market Analytics</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Nigerian procurement market intelligence</p>
      </div>
      <div className="card" style={{ textAlign: 'center', padding: '60px 40px', borderStyle: 'dashed' }}>
        <div style={{ width: '64px', height: '64px', background: 'rgba(79,142,247,0.08)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4f8ef7" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
        </div>
        <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>No analytics data yet</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', maxWidth: '380px', margin: '0 auto 24px', lineHeight: '1.6' }}>
          Analytics will populate once tenders are tracked and activity is recorded. Browse tenders and save the ones relevant to your company to start building your intelligence feed.
        </p>
        <a href="/#/tenders" className="btn btn-primary" style={{ display: 'inline-flex', textDecoration: 'none' }}>Browse Tenders →</a>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '26px', marginBottom: '6px' }}>Market Analytics</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
          Nigerian procurement market intelligence
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '32px' }}>
        {[
          { label: 'Total Tenders', value: data?.summary?.total_tenders },
          { label: 'Open Tenders', value: data?.summary?.open_tenders },
          { label: 'Total Market Value', value: data?.summary?.total_market_value_formatted },
        ].map(({ label, value }) => (
          <div key={label} className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '28px', fontFamily: 'Syne', fontWeight: '800', color: 'var(--accent-green)', marginBottom: '6px' }}>
              {value}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* By Category Bar Chart */}
        <div className="card">
          <h3 style={{ fontSize: '16px', marginBottom: '20px' }}>Tenders by Category</h3>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={data?.by_category} layout="vertical" margin={{ left: 20, right: 16 }}>
              <XAxis type="number" tick={{ fill: '#8892b0', fontSize: 11 }} />
              <YAxis
                dataKey="category"
                type="category"
                width={130}
                interval={0}
                tick={(props) => {
                  const { x, y, payload } = props;
                  const full = payload.value || '';
                  // Long, messy category strings (raw scraped text, not a
                  // short fixed set) is a real, separate data-quality issue
                  // upstream — this just keeps the chart itself readable
                  // regardless of that: truncate what's drawn, keep the
                  // full text one hover away in the Tooltip below.
                  const short = full.length > 18 ? full.slice(0, 17) + '…' : full;
                  return (
                    <text x={x} y={y} dy={4} textAnchor="end" fill="#8892b0" fontSize={11}>
                      <title>{full}</title>{short}
                    </text>
                  );
                }}
              />
              <Tooltip
                contentStyle={{ background: '#141d2e', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px' }}
                labelStyle={{ color: '#f0f4ff' }}
              />
              <Bar dataKey="count" fill="#00d4a1" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* By State Pie Chart */}
        <div className="card">
          <h3 style={{ fontSize: '16px', marginBottom: '20px' }}>Tenders by State</h3>
          <ResponsiveContainer width="100%" height={340}>
            <PieChart>
              <Pie
                data={data?.by_state}
                dataKey="count"
                nameKey="state"
                cx="50%" cy="46%"
                outerRadius={95}
                // Inline labels on a pie with several thin slices collide
                // by design once slice sizes are this uneven (one ~80%
                // slice next to several 1-4% ones) — recharts doesn't
                // avoid that on its own. A legend does the same job
                // without ever overlapping, regardless of slice count.
                label={false}
              >
                {data?.by_state?.map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Legend
                layout="vertical"
                align="right"
                verticalAlign="middle"
                formatter={(value, entry) => {
                  const total = (data?.by_state || []).reduce((s, r) => s + (r.count || 0), 0);
                  const pct = total ? Math.round((entry.payload.count / total) * 100) : 0;
                  return <span style={{ color: '#c7ccdb', fontSize: 12 }}>{value} {pct}%</span>;
                }}
              />
              <Tooltip
                contentStyle={{ background: '#141d2e', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Top Agencies */}
        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '20px' }}>Top Agencies by Contract Value</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {data?.by_agency?.map((agency, i) => (
              <div key={agency.agency} style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '24px', color: 'var(--text-muted)', fontSize: '13px', textAlign: 'right' }}>{i + 1}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '500' }}>{agency.agency}</span>
                    <span style={{ fontSize: '13px', color: 'var(--accent-green)' }}>{agency.total_value_formatted}</span>
                  </div>
                  <div style={{ height: '4px', background: 'var(--bg-secondary)', borderRadius: '2px' }}>
                    <div style={{
                      height: '100%',
                      width: `${(agency.count / (data?.by_agency?.[0]?.count || 1)) * 100}%`,
                      background: COLORS[i % COLORS.length],
                      borderRadius: '2px'
                    }} />
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', width: '50px', textAlign: 'right' }}>
                  {agency.count} tenders
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}